import { ButtonComponent } from '@/components/commons'
import { Title } from '@/components/commons/typography/Title'
import { PlusIcon } from '@/public/assets/v2/images/icons'
import { cssClass } from '@/shared/utils'
import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'

import { useEffect, useMemo } from 'react'
import { joinWithSlash } from 'utilities/links'
import { ProjectCard } from './project-card'
import { ProjectEmptyState } from './ProjectsEmptyState'
import { useProjects } from '../hooks/useProjects'
import { useProjectCreationStore } from '../store/project'
import Masonry from 'react-masonry-css'
import { ProjectStateTypes } from '../types/project'
import { TemplateSelector } from './templates/TemplateSelector'

interface ProjectListSectionProps {
  showFullList?: boolean
}

export const ProjectListSection = ({
  showFullList,
}: ProjectListSectionProps) => {
  const { profile } = useProfile()
  const { setProject } = useProjectCreationStore()
  const ownProfile = useIsOwnProfile()
  const { data: projects } = useProjects()

  const breakpointColumnsObj = {
    default: showFullList ? 3 : 2,
    1250: 2,
    600: 1,
  }

  useEffect(() => {
    setProject(undefined)
  }, [setProject])

  const filteredList = useMemo(() => {
    if (ownProfile) return projects || []

    return (projects || []).filter(
      (project) => project.state === ProjectStateTypes.PUBLISHED
    )
  }, [ownProfile, projects])

  const projectsList = useMemo(() => {
    if (showFullList) return filteredList

    return filteredList?.slice(0, 4)
  }, [filteredList, showFullList])

  const title = showFullList
    ? ownProfile
      ? 'Your Projects'
      : `${profile?.firstName}'s Projects`
    : `Projects`

  const showTitle = showFullList || ownProfile || (projects?.length || 0) > 0

  const allProjects = useMemo(
    () => profile?.projects?.length || 0,
    [profile?.projects]
  )

  if (ownProfile && projects?.length === 0) {
    return (
      <div
        className={cssClass(['px-20 px-0--tablet', showFullList && 'mt-20'])}
      >
        {showTitle && (
          <Title sizeClass="text-16 text-20--tablet">{title}</Title>
        )}
        <div className="mt-20">
          <ProjectEmptyState />
        </div>
      </div>
    )
  }

  return (
    <div className={cssClass(['px-20 px-0--tablet', showFullList && 'mt-20'])}>
      <div className="flex justify-space-between items-center">
        {showTitle && (
          <Title sizeClass="text-16 text-20--tablet">{title}</Title>
        )}

        {ownProfile && (
          <div className="flex gap-12">
            <ButtonComponent
              size="xSmall"
              label="Add"
              to={joinWithSlash(
                profile?.username?.toLocaleLowerCase(),
                'projects',
                'create'
              )}
              iconLeft={<PlusIcon className="text-14 text-secondary" />}
            />

            <div>
              <TemplateSelector />
            </div>
          </div>
        )}
      </div>

      <div className="mt-20">
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="creatorland-masonry-grid"
          columnClassName="creatorland-masonry-grid_column"
        >
          {projectsList?.map((project) => (
            <ProjectCard key={'project_' + project.id} project={project} />
          ))}
        </Masonry>
      </div>

      {((ownProfile && allProjects > 0) || projects?.length > 0) &&
        !showFullList && (
          <ButtonComponent
            variation="white-bg"
            size="xSmall"
            className="mt-20"
            fullWidth
            to={joinWithSlash(
              profile?.username.toLocaleLowerCase(),
              'projects'
            )}
            label={`View All (${
              ownProfile
                ? allProjects.toLocaleString()
                : projects?.length.toLocaleString()
            })`}
          />
        )}
    </div>
  )
}
